/**
 * CR228: Ambiguous Deals Block
 * CR284: Admin-only rendering - never show on Public Site
 * 
 * Renders low-confidence deals as Deal Cards (not legacy list).
 * Uses the same DealCardUXProvider and templates as verified deals.
 * 
 * Layout Studio controls:
 * - Whether this block appears at all (enabled/disabled)
 * - Block order relative to other blocks
 * - Collapsible accordion behavior
 * - Section title text
 * 
 * VISIBILITY RULES (CR284):
 * - Public Site: NEVER render (regardless of layout config)
 * - Admin UI: render if ambiguous deals exist
 * - Live Preview Admin (Prod): render if ambiguous deals exist
 * - Live Preview Live (Public): NEVER render
 */

import { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import type { BlockProps } from "./types";
import { DealGridWithDetails } from "@/components/deals/DealGridWithDetails";
import { DealCardUXProvider } from "@/contexts/DealCardUXContext";
import { cn } from "@/lib/utils";

export interface AmbiguousDealsBlockConfig {
  /** Section title (default: "Unverified / Ambiguous Deals") */
  title?: string;
  /** Description text */
  description?: string;
  /** Whether to start expanded (default: false) */
  defaultExpanded?: boolean;
  /** Whether to use accordion (collapsible) behavior */
  collapsible?: boolean;
}

export function AmbiguousDealsBlock({ config, context }: BlockProps<AmbiguousDealsBlockConfig>) {
  const title = config?.title ?? "Unverified / Ambiguous Deals";
  const description = config?.description ?? "These deals could not be fully verified. They may be expired, have unclear dates, or low confidence.";
  const defaultExpanded = config?.defaultExpanded ?? false;
  const collapsible = config?.collapsible ?? true;
  
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // Filter to only low-confidence deals
  const ambiguousDeals = useMemo(() => {
    return context.deals.filter((d) => d.confidence === "Low");
  }, [context.deals]);
  
  // CR284: Admin-only gate - never render on Public Site or Live Preview "Live" mode
  // context.isAdmin is set by LayoutRenderer based on view context:
  // - Public Site (Index.tsx): isAdmin = false
  // - Admin UI routes: isAdmin = true  
  // - Live Preview Admin (Prod): isAdmin = true
  // - Live Preview Live (Public): isAdmin = false
  if (!context.isAdmin) {
    return null;
  }
  
  // Don't render if no ambiguous deals
  if (ambiguousDeals.length === 0) {
    return null;
  }
  
  const content = (
    <div className="space-y-4">
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      
      {/* Render as Deal Cards using same templates */}
      <DealCardUXProvider 
        cardSize={context.dealCardUX.cardSize} 
        density={context.dealCardUX.density}
        detailsMode={context.dealCardUX.detailsMode}
        expandSingleOpen={context.dealCardUX.expandSingleOpen}
        detailsSections={context.dealCardUX.detailsSections}
        customHeightsEnabled={context.dealCardUX.customHeightsEnabled}
        customHeightsPx={context.dealCardUX.customHeightsPx}
        flyerInteraction={context.dealCardUX.flyerInteraction}
        feedbackWidget={context.dealCardUX.feedbackWidget}
      >
        <DealGridWithDetails
          deals={ambiguousDeals}
          isAdmin={context.isAdmin}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        />
      </DealCardUXProvider>
    </div>
  );
  
  // Non-collapsible mode
  if (!collapsible) {
    return (
      <div className="border border-border rounded-lg bg-muted/30 p-4">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-serif font-medium text-foreground">
            {title}
          </h3>
          <span className="text-sm text-muted-foreground">
            ({ambiguousDeals.length})
          </span>
        </div>
        {content}
      </div>
    );
  }
  
  // Collapsible accordion mode
  return (
    <div className="border border-border rounded-lg bg-muted/30 overflow-hidden">
      {/* Accordion Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-muted-foreground" />
          <span className="font-serif font-medium text-foreground">
            {title}
          </span>
          <span className="text-sm text-muted-foreground">
            ({ambiguousDeals.length})
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>

      {/* Accordion Content */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-200",
          isExpanded ? "max-h-[4000px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="p-4 pt-0">
          {content}
        </div>
      </div>
    </div>
  );
}
